import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api';
import { createOAuthState } from '../utils/oauthState';

interface User {
  id: number;
  username: string;
  email: string;
  phone?: string;
}

interface KakaoUser {
  kakao_id: string;
  nickname: string | null;
  email: string | null;
  phone_number: string | null;
  profile_image_url: string | null;
}

interface AuthContextType {
  user: User | null;
  kakaoUser: KakaoUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithKakao: () => Promise<void>;
  handleKakaoCallback: (code: string, consentMarketing?: boolean) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  updateUser: (updatedUser: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [kakaoUser, setKakaoUser] = useState<KakaoUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 초기 로드 시 localStorage에서 사용자 정보 복원
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedKakaoUser = localStorage.getItem('kakao_user');
        const accessToken = localStorage.getItem('access_token');

        if (storedUser && accessToken) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);

          if (storedKakaoUser) {
            setKakaoUser(JSON.parse(storedKakaoUser));
          }

          // 토큰 유효성 검증 (프로필 조회)
          try {
            const profile = await authApi.getProfile();
            setUser(profile);
          } catch (error) {
            // 토큰 만료 시 로그아웃
            console.error('Token validation failed:', error);
            logout();
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // 카카오 로그인 시작 (인증 페이지로 리다이렉트)
  const loginWithKakao = async () => {
    try {
      // 현재 페이지 URL 저장 (로그인 후 돌아갈 위치)
      // 로그인 화면(/login?reason=… 포함)에서 누른 경우엔 덮어쓰지 않는다 — 들어오기 전에 저장한
      // 돌아갈 곳(예: 상세 '혜택 보기' 버튼의 현장 주소)을 지켜야 한다 (2026-10-01)
      const { pathname } = window.location;
      const currentPath = pathname + window.location.search;

      if (pathname !== '/login' && pathname !== '/auth/kakao/callback') {
        localStorage.setItem('redirect_after_login', currentPath);
      }

      // 이 브라우저에 묶인 1회용 state (콜백에서 대조) — 2026-10-05
      const { auth_url } = await authApi.kakaoGetAuthUrl(createOAuthState());
      window.location.href = auth_url;
    } catch (error) {
      console.error('Failed to get Kakao auth URL:', error);
      throw error;
    }
  };

  // 카카오 콜백 처리
  const handleKakaoCallback = async (code: string, consentMarketing?: boolean) => {
    try {
      const response = await authApi.kakaoCallback(code, consentMarketing);

      // 토큰 및 사용자 정보 저장
      localStorage.setItem('access_token', response.access);
      localStorage.setItem('refresh_token', response.refresh);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('kakao_user', JSON.stringify(response.kakao_user));

      // 동의 정보 정리 (로그인 완료 후)
      localStorage.removeItem('consent_privacy');
      localStorage.removeItem('consent_terms');
      localStorage.removeItem('consent_marketing');
      localStorage.removeItem('consent_date');

      setUser(response.user);
      setKakaoUser(response.kakao_user);
    } catch (error) {
      console.error('Kakao callback failed:', error);
      throw error;
    }
  };

  const logout = () => {
    // localStorage 정리
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('kakao_user');

    setUser(null);
    setKakaoUser(null);
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const refresh = localStorage.getItem('refresh_token');

      if (!refresh) {
        return false;
      }

      const response = await authApi.refreshToken(refresh);
      localStorage.setItem('access_token', response.access);

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  };

  // 사용자 정보 업데이트
  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value: AuthContextType = {
    user,
    kakaoUser,
    isAuthenticated: !!user,
    isLoading,
    loginWithKakao,
    handleKakaoCallback,
    logout,
    refreshToken,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
